import LoadingDisplay from "../common/LoadingDisplay";
import { Table, Form, InputGroup } from "react-bootstrap";
import { TemplateTextModel, templateTextApi } from "../api/TemplateTextApi";
import { Problem } from "../api/backendApi";
import { useState, useEffect } from "react";
import ErrorDisplay from "../common/ErrorDisplay";
import {
  TemplateTextTypeModel,
  templateTextTypeApi,
} from "../api/TemplateTextTypeApi";
import {
  PlusCircleFill,
  Search,
  TrashFill,
  ChevronExpand,
} from "react-bootstrap-icons";
import TemplateTextModal from "../components/modals/TemplateTextModal";
import { authUtils } from "../common/authUtils";
import { useAuth } from "react-oidc-context";
import ContextMenuCopy from "../components/contextmenus/ContextMenuCopy";

function DefaultTemplateTextPage() {
  const auth = useAuth();
  const [templateTexts, setTemplateTexts] = useState<TemplateTextModel[]>([]);
  const [filteredTemplateTexts, setFilteredTemplateTexts] = useState<
    TemplateTextModel[]
  >([]);
  const [templateTextTypes, setTemplateTextTypes] = useState<
    TemplateTextTypeModel[]
  >([]);
  const [typeFilter, setTypeFilter] = useState<number>();
  const [blockNameFilter, setBlockNameFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [modalText, setModalText] = useState("");
  const [show, setShow] = useState(false);
  const [templateTextBeingEdited, setTemplateTextBeingEdited] = useState<
    TemplateTextModel | undefined
  >(undefined);
  const [isCreate, setIsCreate] = useState(false);
  const [newTemplateText, setNewTemplateText] = useState<
    TemplateTextModel | undefined
  >(undefined);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [contextMenuSelected, setContectMenuSelected] =
    useState<TemplateTextModel>();
  const [contextMenuOptions, setContectMenuOptions] = useState<string[]>([]);
  const [sortBlockName, setSortBlockName] = useState<boolean>(false);

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    templateText?: TemplateTextModel,
  ) => {
    setIsCreate(isCreate);
    setModalText(modalText);
    setTemplateTextBeingEdited(templateText);
    setNewTemplateText(templateText);
    setShow(true);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLTableCellElement>) => {
    event.preventDefault();

    const position = {
      top: event.clientY,
      left: event.clientX,
    };

    setContextMenuPosition(position);
    setIsContextMenuVisible(true);
  };

  const selectTemplateText = (templateText: TemplateTextModel) => {
    if (templateText !== templateTextBeingEdited) {
      setTemplateTextBeingEdited(templateText);
    }
  };

  const onTemplateTextChanged = (templateText: TemplateTextModel) => {
    setError(undefined);
    let updatedTemplateTexts = templateTexts.map((t: TemplateTextModel) => {
      return t === templateTextBeingEdited ? templateText : t;
    });

    setTemplateTexts(updatedTemplateTexts);
    filterTemplateTexts();
    setTemplateTextBeingEdited(undefined);
  };

  const onNewDefaultTemplateText = (templateText: TemplateTextModel) => {
    setTemplateTexts([...templateTexts, templateText]);

    setNewTemplateText(undefined);

    setTimeout(() => {
      var scrollingElement = document.querySelector("div.fixed-table-header");
      scrollingElement?.scrollTo(0, scrollingElement?.scrollHeight);
    }, 50);
  };

  useEffect(() => {
    async function loadTemplateTexts() {
      setLoading(true);
      const data = await templateTextApi.getTemplateTexts();

      if (data.ok) {
        setError(undefined);
        setTemplateTexts(data.value);
        setFilteredTemplateTexts(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadTemplateTexts();
  }, []);

  useEffect(() => {
    async function loadTemplateTextTypes() {
      setLoading(true);
      const data = await templateTextTypeApi.getTemplateTextTypes();

      if (data.ok) {
        setError(undefined);
        setTemplateTextTypes(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadTemplateTextTypes();
  }, []);

  const filterTemplateTexts = () => {
    var filteredTemplateTexts = [...templateTexts];

    if (templateTextTypes.some((textType) => textType.id === typeFilter)) {
      filteredTemplateTexts = filteredTemplateTexts.filter(
        (templateText) => templateText.defaultTemplateTextTypeId === typeFilter,
      );
    }

    filteredTemplateTexts = filteredTemplateTexts.filter(
      (templateText) =>
        templateText.dxfBlockName
          ?.toUpperCase()
          .includes(blockNameFilter.toUpperCase()),
    );

    setFilteredTemplateTexts(filteredTemplateTexts);
  };

  const deleteTemplateText = (defaultTemplateText: TemplateTextModel) => {
    templateTextApi
      .deleteTemplateText(
        defaultTemplateText.dxfBlockName,
        defaultTemplateText.defaultTemplateTextTypeId,
      )
      .then((response) => {
        if (response.ok) {
          setTemplateTexts(
            templateTexts.filter((t) => t !== defaultTemplateText),
          );
        } else {
          setError(response.problem);
        }
      });
  };

  const sortArray = (getter: any, order: boolean) => {
    const sortedArray = [...templateTexts];

    sortedArray.sort((a, b) =>
      getter(a) !== undefined && getter(b) !== undefined
        ? order === true
          ? getter(b).localeCompare(getter(a))
          : getter(a).localeCompare(getter(b))
        : 0,
    );

    setTemplateTexts(sortedArray);
  };

  useEffect(() => {
    filterTemplateTexts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, templateTexts, blockNameFilter]);

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Template Texts
          {authUtils.isAdministrator(auth) && (
            <PlusCircleFill
              className="text-primary"
              onClick={() => handleShow(true, "Add Template Text", undefined)}
              width={32}
              height={32}
              style={{ marginLeft: "0.5rem" }}
              role="button"
            />
          )}
        </h1>

        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Type
                </Form.Label>
              </span>
              <Form.Select
                onChange={(e) => setTypeFilter(Number(e.target.value))}
                style={{ display: "flex", flexDirection: "row" }}
              >
                <option>Select Type</option>
                {templateTextTypes.map((defaultTemplateTextType) => (
                  <option
                    key={defaultTemplateTextType.id}
                    value={defaultTemplateTextType.id}
                  >
                    {defaultTemplateTextType.name}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Block name
                </Form.Label>
              </span>
              <Form.Control
                size="sm"
                onChange={(e) => {
                  setBlockNameFilter(e.target.value);
                }}
              ></Form.Control>
            </InputGroup>
          </div>
        )}
        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "95%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover>
                <thead>
                  <tr>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        className="table-header"
                        onClick={() => {
                          sortArray(
                            (g: TemplateTextModel) => g.dxfBlockName,
                            sortBlockName,
                          );
                          setSortBlockName(!sortBlockName);
                        }}
                      >
                        Block name
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Crs name
                      </Form.Label>
                    </th>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Template Text type
                      </Form.Label>
                    </th>
                    <th style={{ width: "1px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplateTexts.map((defaultTemplateText, index) => (
                    <tr
                      role="button"
                      key={index}
                      onClick={() => {
                        if (authUtils.isAdministrator(auth)) {
                          handleShow(
                            false,
                            "Edit Template Text",
                            defaultTemplateText,
                          );
                          selectTemplateText(defaultTemplateText);
                        }
                      }}
                    >
                      <td
                        onContextMenu={(e) => {
                          handleContextMenu(e);
                          setContectMenuSelected(defaultTemplateText);
                          let options: string[] = ["Create Copy"];

                          setContectMenuOptions(options);
                        }}
                      >
                        {defaultTemplateText.dxfBlockName}
                      </td>
                      <td>{defaultTemplateText.crsName}</td>
                      <td>
                        {
                          templateTextTypes.find(
                            (t) =>
                              t.id ===
                              (defaultTemplateText.defaultTemplateTextTypeId ==
                              null
                                ? 0
                                : defaultTemplateText.defaultTemplateTextTypeId),
                          )?.name
                        }
                      </td>
                      <td>
                        {authUtils.isAdministrator(auth) && (
                          <TrashFill
                            className="text-danger"
                            height={24}
                            width={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("Are you sure?")) {
                                deleteTemplateText(defaultTemplateText);
                              }
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show && authUtils.isAdministrator(auth) ? (
        <TemplateTextModal
          templateText={
            newTemplateText ?? {
              dxfBlockName: undefined,
              crsName: undefined,
              crsInfo: undefined,
              defaultTemplateTextTypeId: undefined,
            }
          }
          isCreate={isCreate}
          templateTextTypes={templateTextTypes}
          onChange={isCreate ? onNewDefaultTemplateText : onTemplateTextChanged}
          onCancel={() => {
            setNewTemplateText(undefined);
            setShow(false);
          }}
          modalText={modalText}
        />
      ) : null}

      {isContextMenuVisible && authUtils.isAdministrator(auth) && (
        <ContextMenuCopy
          onClose={() => setIsContextMenuVisible(false)}
          onCreateCopy={() =>
            handleShow(true, "Add Template Text", {
              dxfBlockName: contextMenuSelected?.dxfBlockName,
              crsName: contextMenuSelected?.crsName,
              crsInfo: contextMenuSelected?.crsInfo,
              defaultTemplateTextTypeId: undefined,
            })
          }
          options={contextMenuOptions}
          style={{
            position: "absolute",
            top: contextMenuPosition.top,
            left: contextMenuPosition.left,
          }}
        />
      )}
    </>
  );
}

export default DefaultTemplateTextPage;
